/**
 * IndexerPage (epic-6 §5 / IDX-5/6, UC-7) — route /indexer.
 *
 * Tabs:
 *  - Sources: git/collection source CRUD, status, watermark sha, last sync,
 *    "Sync now" (git sources)
 *  - Graph: hierarchical SVG of GET /indexer/graph (no new deps); nodes open
 *    a detail drawer
 *  - Search: query + source filter + rerank toggle → unified search results
 *    (score/similarity + rerankScore + content snippet) linking to the source
 *    file in storage/repositories.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Trash2, RefreshCw, Search, Database, GitBranch, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DataTable } from '@/components/shared/DataTable';
import { EmptyState } from '@/components/shared/EmptyState';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { useToast } from '@/components/shared/Toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { type ColumnDef } from '@tanstack/react-table';
import {
  useGetIndexerSourcesQuery,
  useCreateIndexerSourceMutation,
  useUpdateIndexerSourceMutation,
  useDeleteIndexerSourceMutation,
  useSyncRepositoryMutation,
  useGetIndexerGraphQuery,
  useSearchIndexerMutation,
} from '@/store/api/indexerApi';
import { useGetStorageCollectionsQuery } from '@/store/api/apiSlice';
import { GraphView } from '@/components/indexer/GraphView';
import type { IndexerSource, IndexerGraphNode, IndexerSearchHit } from '@/types';
import { formatDistanceToNow } from 'date-fns';

export function IndexerPage() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Indexer</h1>
        <p className="text-sm text-muted-foreground">
          Sources, dependency graph and unified search (UC-7)
        </p>
      </div>

      <Tabs defaultValue="sources">
        <TabsList>
          <TabsTrigger value="sources"><Database className="mr-1.5 h-3.5 w-3.5" /> Sources</TabsTrigger>
          <TabsTrigger value="graph"><GitBranch className="mr-1.5 h-3.5 w-3.5" /> Graph</TabsTrigger>
          <TabsTrigger value="search"><Search className="mr-1.5 h-3.5 w-3.5" /> Search playground</TabsTrigger>
        </TabsList>
        <TabsContent value="sources"><SourcesTab /></TabsContent>
        <TabsContent value="graph"><GraphTab /></TabsContent>
        <TabsContent value="search"><SearchTab /></TabsContent>
      </Tabs>
    </div>
  );
}

// ─── Sources tab ──────────────────────────────────────────────────────────────

function SourcesTab() {
  const toast = useToast();
  const { data: sources, isLoading } = useGetIndexerSourcesQuery();
  const { data: collections } = useGetStorageCollectionsQuery();
  const [createSource] = useCreateIndexerSourceMutation();
  const [updateSource] = useUpdateIndexerSourceMutation();
  const [deleteSource] = useDeleteIndexerSourceMutation();
  const [syncRepo, { isLoading: isSyncing }] = useSyncRepositoryMutation();

  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<IndexerSource | null>(null);
  const [form, setForm] = useState({ collectionId: '', mediaEnabled: true });

  const sourceList = (sources as IndexerSource[] | undefined) ?? [];
  const collectionList = (collections as Array<{ _id: string; name: string }> | undefined) ?? [];

  const columns: ColumnDef<IndexerSource, unknown>[] = [
    {
      accessorKey: 'type',
      header: 'Type',
      cell: ({ row }) => <Badge variant="outline" className="text-xs">{row.original.type}</Badge>,
    },
    {
      accessorKey: 'collectionId',
      header: 'Target',
      cell: ({ row }) => {
        const s = row.original;
        const col = collectionList.find((c) => c._id === s.collectionId);
        if (s.type === 'git') {
          return <span className="font-mono text-xs">{s.branchName ?? 'default'}</span>;
        }
        return <span className="text-xs">{col?.name ?? s.collectionId?.slice(0, 10) ?? '—'}</span>;
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => <Badge variant={row.original.status === 'error' ? 'destructive' : row.original.status === 'syncing' ? 'secondary' : 'default'} className="text-xs">{row.original.status}</Badge>,
    },
    {
      accessorKey: 'lastSyncedCommitSha',
      header: 'Watermark',
      cell: ({ row }) => (
        <span className="font-mono text-[11px] text-muted-foreground">
          {row.original.lastSyncedCommitSha ? row.original.lastSyncedCommitSha.slice(0, 10) : '—'}
        </span>
      ),
    },
    {
      accessorKey: 'lastSyncAt',
      header: 'Last sync',
      cell: ({ row }) =>
        row.original.lastSyncAt ? (
          <span className="text-xs">{formatDistanceToNow(new Date(row.original.lastSyncAt), { addSuffix: true })}</span>
        ) : (
          <span className="text-xs text-muted-foreground">never</span>
        ),
    },
    {
      accessorKey: 'isActive',
      header: 'Active',
      cell: ({ row }) => <Badge variant={row.original.isActive ? 'default' : 'secondary'} className="text-xs">{row.original.isActive ? 'yes' : 'no'}</Badge>,
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        const s = row.original;
        return (
          <div className="flex gap-1">
            {s.type === 'git' && s.repositoryId && (
              <Button
                variant="ghost"
                size="icon"
                title="Sync now"
                disabled={isSyncing || s.status === 'syncing'}
                onClick={() =>
                  syncRepo({ id: s.repositoryId!, branchName: s.branchName })
                    .unwrap()
                    .then((r) => toast.success(`Sync queued — ${r.totalCommitsSynced} new commit(s)`))
                    .catch((err) => toast.error((err as { data?: { message?: string } })?.data?.message ?? 'Sync failed'))
                }
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              title={s.isActive ? 'Deactivate' : 'Activate'}
              onClick={() =>
                updateSource({ id: s._id, body: { isActive: !s.isActive } })
                  .unwrap()
                  .catch((err) => toast.error((err as { data?: { message?: string } })?.data?.message ?? 'Update failed'))
              }
            >
              <Database className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => setDeleteTarget(s)}>
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </div>
        );
      },
    },
  ];

  const submitCreate = async () => {
    if (!form.collectionId) {
      toast.error('Select a storage collection');
      return;
    }
    try {
      await createSource({ collectionId: form.collectionId, mediaEnabled: form.mediaEnabled }).unwrap();
      setCreateOpen(false);
      setForm({ collectionId: '', mediaEnabled: true });
      toast.success('Source created');
    } catch (err) {
      toast.error((err as { data?: { message?: string } })?.data?.message ?? 'Failed to create source');
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="text-sm">Indexer sources</CardTitle>
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          <Plus className="mr-1 h-4 w-4" /> Add collection source
        </Button>
      </CardHeader>
      <CardContent className="pt-0">
        {isLoading ? (
          <LoadingSpinner className="py-8" />
        ) : sourceList.length === 0 ? (
          <EmptyState title="No sources" description="Add a storage-collection source to start indexing" />
        ) : (
          <DataTable columns={columns} data={sourceList} />
        )}
      </CardContent>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add collection source</DialogTitle>
            <DialogDescription>Index the files of a storage collection (media optional).</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Storage collection</Label>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={form.collectionId}
                onChange={(e) => setForm({ ...form, collectionId: e.target.value })}
              >
                <option value="">— select collection —</option>
                {collectionList.map((c) => (
                  <option key={c._id} value={c._id}>{c.name}</option>
                ))}
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.mediaEnabled}
                onChange={(e) => setForm({ ...form, mediaEnabled: e.target.checked })}
                className="h-4 w-4 rounded border-input accent-primary"
              />
              Include media files
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={submitCreate}>Create source</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Delete Source"
        description="Remove this indexer source? Indexed data is not deleted."
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={async () => {
          if (deleteTarget) {
            try {
              await deleteSource(deleteTarget._id).unwrap();
              setDeleteTarget(null);
            } catch (err) {
              toast.error((err as { data?: { message?: string } })?.data?.message ?? 'Failed to delete');
            }
          }
        }}
      />
    </Card>
  );
}

// ─── Graph tab ───────────────────────────────────────────────────────────────

function GraphTab() {
  const { data: graph, isLoading } = useGetIndexerGraphQuery();
  const [selected, setSelected] = useState<IndexerGraphNode | null>(null);

  return (
    <div className="space-y-3">
      <GraphView graph={graph as { nodes: IndexerGraphNode[]; edges: { from: string; to: string; kind: 'writes' | 'reads' | 'derived' }[] } | undefined} loading={isLoading} onSelectNode={setSelected} />
      <p className="text-xs text-muted-foreground">
        Hierarchical layout: sources → tables → stats. Click a node for details (IDX-5).
      </p>
      <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selected?.label}</DialogTitle>
            <DialogDescription>
              <Badge variant="outline" className="text-[10px]">{selected?.type}</Badge> · {selected?.id}
            </DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="space-y-1 text-sm">
              <div className="flex justify-between rounded border px-3 py-1.5">
                <span className="text-muted-foreground">Chunks</span>
                <span className="font-mono">{selected.stats.chunks ?? '—'}</span>
              </div>
              <div className="flex justify-between rounded border px-3 py-1.5">
                <span className="text-muted-foreground">Files</span>
                <span className="font-mono">{selected.stats.files ?? '—'}</span>
              </div>
              <div className="flex justify-between rounded border px-3 py-1.5">
                <span className="text-muted-foreground">Last sync</span>
                <span className="font-mono">{selected.stats.lastSync ? new Date(selected.stats.lastSync).toLocaleString() : '—'}</span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Search playground ────────────────────────────────────────────────────────

function SearchTab() {
  const toast = useToast();
  const [search, { data, isLoading }] = useSearchIndexerMutation();
  const [query, setQuery] = useState('');
  const [sourceType, setSourceType] = useState<'git' | 'collection' | ''>('');
  const [rerank, setRerank] = useState(false);

  const results = (data as { results?: IndexerSearchHit[] } | undefined)?.results ?? [];
  const pagination = (data as { pagination?: { totalCandidates: number } } | undefined)?.pagination;

  const runSearch = () => {
    if (!query.trim()) {
      toast.error('Enter a query');
      return;
    }
    search({
      query: query.trim(),
      sourceType: sourceType || undefined,
      rerank,
      limit: 25,
    }).unwrap();
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="space-y-3 pt-5">
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[240px] flex-1 space-y-1">
              <Label className="text-xs">Query</Label>
              <input
                autoFocus
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                placeholder="semantic search over indexed sources…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && runSearch()}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Source type</Label>
              <select className="h-10 rounded-md border border-input bg-background px-2 text-sm" value={sourceType} onChange={(e) => setSourceType(e.target.value as typeof sourceType)}>
                <option value="">All</option>
                <option value="git">Git</option>
                <option value="collection">Collection</option>
              </select>
            </div>
            <label className="flex h-10 items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={rerank}
                onChange={(e) => setRerank(e.target.checked)}
                className="h-4 w-4 rounded border-input accent-primary"
              />
              Rerank
            </label>
            <Button onClick={runSearch} disabled={isLoading || !query.trim()}>
              <Search className="mr-1.5 h-4 w-4" /> {isLoading ? 'Searching…' : 'Search'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {data && (
        <p className="text-xs text-muted-foreground">
          {results.length} result(s) {pagination?.totalCandidates !== undefined ? `from ${pagination.totalCandidates} candidate(s)` : ''}
          {rerank ? ' · reranked' : ''}
        </p>
      )}

      {results.length === 0 && data ? (
        <EmptyState title="No matches" description="Try a different query or enable rerank" />
      ) : (
        <div className="space-y-2">
          {results.map((hit, i) => (
            <Card key={`${hit.filePath}-${hit.chunk_index ?? i}`}>
              <CardContent className="space-y-2 pt-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    {hit.sourceType === 'git' ? <GitBranch className="h-4 w-4 shrink-0 text-muted-foreground" /> : <Database className="h-4 w-4 shrink-0 text-muted-foreground" />}
                    <span className="font-mono text-xs font-medium truncate">{hit.filePath}</span>
                    {hit.sourceKey && <Badge variant="outline" className="text-[10px]">{hit.sourceKey}</Badge>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge variant="secondary" className="text-[10px] font-mono">
                      score {hit.similarity !== undefined ? hit.similarity.toFixed(3) : hit.score?.toFixed(3) ?? '—'}
                    </Badge>
                    {hit.rerankScore !== undefined && (
                      <Badge variant="outline" className="text-[10px] font-mono">rerank {hit.rerankScore.toFixed(3)}</Badge>
                    )}
                  </div>
                </div>
                <p className="line-clamp-3 rounded border bg-muted/40 px-3 py-2 text-xs text-muted-foreground font-mono whitespace-pre-wrap">
                  {hit.content}
                </p>
                <div className="flex justify-end">
                  <SourceFileLink hit={hit} />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function SourceFileLink({ hit }: { hit: IndexerSearchHit }) {
  if (hit.sourceType === 'collection' || hit.sourceKey) {
    return (
      <Link to={`/storage/collections`} className="text-xs text-muted-foreground hover:text-primary">
        open in storage <ExternalLink className="inline h-3 w-3" />
      </Link>
    );
  }
  return (
    <Link to={`/repositories`} className="text-xs text-muted-foreground hover:text-primary">
      open in repositories <ExternalLink className="inline h-3 w-3" />
    </Link>
  );
}